import { DataTypes } from '@/enums/DataTypes';
import { FamilyResidentAccess } from '@/enums/FamilyResidentAccess';
import { FamilyResidentSettings } from '@/enums/FamilyResidentSettings';
import * as firebase from 'firebase/app';

export const familyResidentSettingsTypeMap: Record<string, string> = {
  [FamilyResidentSettings.ResidentConsent]: DataTypes.Boolean,
};

export const getDefaultFamilyResidentAccess = (facility: any, defaultValue: boolean = null) =>{

  const access = {
    activities: true,
    care: facility?.isEnableCareTab ?? false,
    careNotes:  false,
    careSummary: facility?.isCareSummary ?? false,
    chat: true,
    concierge: true,
    conciergeEvents: true,
    dailyCare: facility?.isDailyCare ?? false,
    dailyPlan: facility?.isDailyPlan ?? false,
    carePlan: (facility?.isCarePlan && facility?.isEnabledCarePlan) ?? false,
    instantCalls: true,
    lifestyle: true,
    lifestyleEvents: true,
    noticeboard: true,
    scheduledCalls: true
  }

  if(typeof(defaultValue) === 'boolean'){
    access.activities = defaultValue
    access.care = facility?.isEnableCareTab === true && defaultValue === true ? defaultValue : false
    access.careNotes = defaultValue
    access.careSummary = facility?.isCareSummary === true && defaultValue === true ? defaultValue: false
    access.chat = defaultValue
    access.concierge = defaultValue
    access.conciergeEvents = defaultValue
    access.dailyCare = facility?.isDailyCare === true && defaultValue === true ? defaultValue: false
    access.dailyPlan = facility?.isDailyPlan === true && defaultValue === true ? defaultValue: false
    access.carePlan = (facility?.isCarePlan === true && facility?.isEnabledCarePlan === true && defaultValue === true)  ? defaultValue: false
    access.instantCalls = defaultValue
    access.lifestyle = defaultValue
    access.lifestyleEvents = defaultValue
    access.noticeboard = defaultValue
    access.scheduledCalls = defaultValue

    return access
  }

 return access
}

export const getDefaultFamilyResidentSettings = () =>{
  return  {
     residentConsent: false,
   }
 }

export const fillMissingFamilyResidentAccess = (
   currentAccesses: {
     residentId: string;
     name: string;
     isEnabled: boolean;
     isEnabledByStaff: boolean;
   }[],
   residentId: string,
 ) => {
   const existingAccessNames = new Set(
     currentAccesses
       .filter(a => a.residentId === residentId)
       .map(a => a.name)
   );
 
   const completedAccesses = [...currentAccesses];
 
   for (const name of Object.values(FamilyResidentAccess)) {
     if (!existingAccessNames.has(name)) {
       completedAccesses.push({
         residentId,
         name,
         isEnabled: false,
         isEnabledByStaff: false,
       });
     }
   }
 
   return completedAccesses;
 };

 export const fillMissingFamilyResidentSettings = (
   currentSettings: {
     residentId: string;
     name: string;
     value: any;
     type: string;
   }[],
   residentId: string
 ) => {
   const existingAccessNames = new Set(
     currentSettings.filter(a => a.residentId === residentId).map(a => a.name)
   );

   const completedAccessesSettings = [...currentSettings];
 
   for (const name of Object.values(FamilyResidentSettings)) {
     if (!existingAccessNames.has(name)) {
       let value: any;
 
       const type = familyResidentSettingsTypeMap[name]

       if (!type) {
         throw new Error(`Unknown setting name "${name}" — cannot determine its type. Please add it to the typeMap.`);
       }
 
       switch (type) {
         case DataTypes.Boolean:
           value = false;
           break;
         case DataTypes.String:
           value = '';
           break;
         case DataTypes.Number:
           value = 0;
           break;
         default:
           value = null;
       }
 
       completedAccessesSettings.push({ residentId, name, type, value });
     }
   }
 
   return completedAccessesSettings;
 };

 export const mapFamilyAccessSettings = (input: Record<string, boolean>, facilityId: string, residentId: string): {
  residentId: string;
  name: string;
  isEnabled: boolean;
  isEnabledByStaff: boolean;
}[] => {
  return Object.entries(input).map(([key, value]) => ({
    facilityId: facilityId,
    residentId,
    name: key,
    isEnabled: value,
    isEnabledByStaff: value,
  }));
}

export const upsertFamilyResidentSettingsForStaff = async (params: {
  familyMemberId: string;
  residentId: string;
  name: string;
  facilityId: string;
  isEnabledByStaff: boolean;
}): Promise<void> => {

  const docRef = firebase
    .firestore()
    .collection('familyHubUsers')
    .doc(params.familyMemberId)
    .collection('familyResidentAccess')
    .doc(`${params.residentId}_${params.name}`);

  const docSnapshot = await docRef.get();
  const existingData = docSnapshot.exists ? docSnapshot.data() : {};
  const preservedIsEnabled = existingData?.isEnabled ?? true;

  await docRef.set({
      isEnabledByStaff: params.isEnabledByStaff,
      isEnabled: preservedIsEnabled,
      residentId: params.residentId,
      facilityId: params.facilityId,
    },{ merge: true });
};
